import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { collectionKeys } from "@/lib/query-keys";
import type {
  CollectionExport,
  ExportHistoryScope,
  ReportCollection,
  ReportCollectionItem,
  ReportFormat,
} from "@/types/api";

function base(projectId: string, collectionId: string) {
  return `/reports/3.0/projects/${projectId}/collections/${collectionId}`;
}

/**
 * Add photos or issues to a collection that already exists.
 *
 * Sends the ids being added, never the resulting list. Two people maintain the
 * same recurring report: a request saying "the report is now these twelve"
 * describes it as it looked before the other person's edits and silently undoes
 * them, while "add these three" applies on top of whatever they did. See
 * [[mutations]].
 */
export function useAddCollectionItems(projectId: string, collectionId: string) {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (items: string[]) =>
      fetchWithAuth<ReportCollectionItem[]>(
        `${base(projectId, collectionId)}/items/`,
        { method: "POST", body: JSON.stringify({ items }) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: collectionKeys.items(projectId, collectionId),
      });
      void queryClient.invalidateQueries({
        queryKey: collectionKeys.detail(projectId, collectionId),
      });
      void queryClient.invalidateQueries({
        queryKey: collectionKeys.list(projectId),
      });
    },
  });
}

/**
 * Remove one membership row.
 *
 * The photo or issue itself is untouched and stays in the project — this is a
 * membership change, not a delete.
 */
export function useRemoveCollectionItem(
  projectId: string,
  collectionId: string,
) {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) =>
      fetchWithAuth<void>(`${base(projectId, collectionId)}/items/${itemId}/`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: collectionKeys.items(projectId, collectionId),
      });
      void queryClient.invalidateQueries({
        queryKey: collectionKeys.detail(projectId, collectionId),
      });
      void queryClient.invalidateQueries({
        queryKey: collectionKeys.list(projectId),
      });
    },
  });
}

/**
 * Everything an export may override. Built by `buildExportInput`
 * (see [[export-options]]) rather than assembled at the call site, so the
 * "orientation is meaningless for a deck" rule lives in one testable place.
 */
export interface CreateExportInput {
  template?: string;
  format?: ReportFormat;
  orientation?: string | null;
  history_scope?: ExportHistoryScope | null;
}

/**
 * Export the collection as it stands.
 *
 * Takes the whole collection — there is no partial export. Every setting is
 * optional and falls back to what the collection remembers.
 */
export function useCreateCollectionExport(
  projectId: string,
  collectionId: string,
) {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateExportInput = {}) =>
      fetchWithAuth<CollectionExport>(
        `${base(projectId, collectionId)}/exports/`,
        { method: "POST", body: JSON.stringify(input) },
      ),
    onSuccess: (created) => {
      // Seat the new export before the invalidated refetch lands. Without this
      // there is a 1–3s window on a jobsite link where the POST has returned,
      // `isPending` is false, and the cached collection still names the *old*
      // COMPLETED export — so Export re-enables with no progress bar under it
      // and invites a second tap, which is a second multi-minute backend job.
      // Guarded on an id because a 202 is allowed to come back thin.
      if (created?.id) {
        queryClient.setQueryData<ReportCollection>(
          collectionKeys.detail(projectId, collectionId),
          (current) =>
            current ? { ...current, latest_export: created } : current,
        );
      }
      void queryClient.invalidateQueries({
        queryKey: collectionKeys.exports(projectId, collectionId),
      });
      void queryClient.invalidateQueries({
        queryKey: collectionKeys.detail(projectId, collectionId),
      });
      void queryClient.invalidateQueries({
        queryKey: collectionKeys.list(projectId),
      });
    },
  });
}

/** Rename a collection. The name on past exports is frozen and does not move. */
export function useRenameCollection(projectId: string) {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      fetchWithAuth<ReportCollection>(`${base(projectId, id)}/`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: collectionKeys.detail(projectId, id),
      });
      void queryClient.invalidateQueries({
        queryKey: collectionKeys.list(projectId),
      });
    },
  });
}

/** Delete a collection. Every version under it goes too. */
export function useDeleteCollection(projectId: string) {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      fetchWithAuth<void>(`${base(projectId, id)}/`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: collectionKeys.list(projectId),
      });
    },
  });
}
